export const setInitialised = () => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    carousel.on('created', (carousel) => {
      carousel.container.setAttribute(`data-initialised`, true)
    })
  }
}

/**
 * @param {{
 *  next?: HTMLButtonElement,
 *  previous?: HTMLButtonElement,
 * }} elements
 */
export const useArrows = ({ next, previous } = {}) => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    next?.addEventListener('click', carousel.next)
    previous?.addEventListener('click', carousel.prev)

    const updateButtonStates = () => {
      const { abs, maxIdx, min } = carousel.track.details ?? {}
      const { perView } = carousel.options.slides ?? {}
      const allVisible =
        typeof perView === 'number' && carousel.slides.length <= perView
      const isHidden = carousel.options.disabled || maxIdx === 0 || allVisible

      if (next) {
        next.disabled = abs === maxIdx
        next.setAttribute('aria-hidden', isHidden)
      }

      if (previous) {
        previous.disabled = abs === min
        previous.setAttribute('aria-hidden', isHidden)
      }
    }

    carousel.on('created', updateButtonStates)
    carousel.on('slideChanged', updateButtonStates)
    carousel.on('optionsChanged', updateButtonStates)
  }
}

/**
 * @param {number} seconds
 * @param {{
 *  pauseOnHover?: boolean
 *  pauseOnFocus?: boolean
 * }} options
 */
export const useAutoplay = (
  seconds = 2,
  { pauseOnHover = true, pauseOnFocus = true } = {},
) => {
  const delay = seconds * 1000

  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    let timeout
    let mouseOver = false
    let focusWithin = false

    const clearCurrentTimout = () => clearTimeout(timeout)
    const setNextTimeout = () => {
      clearCurrentTimout()

      if (mouseOver || focusWithin || carousel.options.disabled) {
        return
      }

      timeout = setTimeout(carousel.next, delay)
    }

    carousel.on('created', () => {
      if (pauseOnHover) {
        carousel.container.addEventListener('mouseover', () => {
          mouseOver = true
          clearCurrentTimout()
        })
        carousel.container.addEventListener('mouseout', () => {
          mouseOver = false
          setNextTimeout()
        })
      }

      if (pauseOnFocus) {
        carousel.container.addEventListener('focusin', () => {
          focusWithin = true
          clearCurrentTimout()
        })
        carousel.container.addEventListener('focusout', (event) => {
          if (carousel.container.contains(event.relatedTarget)) return

          focusWithin = false
          setNextTimeout()
        })
      }

      setNextTimeout()
    })

    carousel.on('dragStarted', clearCurrentTimout)
    carousel.on('animationEnded', setNextTimeout)
    carousel.on('updated', setNextTimeout)
    carousel.on('optionsChanged', setNextTimeout)
  }
}

/** @param {HTMLButtonElement[]} dots */
export const useDots = (dots = []) => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    const updateDotStates = () => {
      dots.forEach((dot, index) => {
        const isCurrent = carousel.track.details.rel === index

        dot.setAttribute('aria-current', String(isCurrent))
      })
    }

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        carousel.moveToIdx(index)
      })
    })

    carousel.on('created', updateDotStates)
    carousel.on('slideChanged', updateDotStates)
  }
}

export const addWheelControls = (direction) => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    let touchTimeout = null
    let wheelActive = false
    let position = {}

    carousel?.container?.style?.setProperty(
      direction === 'horizontal'
        ? 'overscroll-behavior-x'
        : 'overscroll-behavior-y',
      'contain',
    )

    const dispatch = (event, name) => {
      position.x -= event.deltaX
      position.y -= event.deltaY
      carousel.container.dispatchEvent(
        new CustomEvent(name, {
          detail: {
            x: position.x,
            y: position.y,
          },
        }),
      )
    }

    const wheelStart = (event) => {
      position = {
        x: event.pageX,
        y: event.pageY,
      }
      dispatch(event, 'ksDragStart')
    }

    const wheel = (event) => {
      dispatch(event, 'ksDrag')
    }

    const wheelEnd = (event) => {
      dispatch(event, 'ksDragEnd')
    }

    const eventWheel = (event) => {
      let supportWheel = true
      let earlyReturn = false
      const { details } = carousel?.track

      /* Ensure we don't block vertical scrolling for horizontal carousels */
      if (
        direction === 'horizontal' &&
        (event?.deltaY > 1 || event?.deltaY < -1)
      ) {
        supportWheel = false
        earlyReturn = true
      }

      /* Ensure we don't block horizontal scrolling for vertical carousels */
      if (
        !earlyReturn &&
        direction === 'vertical' &&
        (event?.deltaX > 1 || event?.deltaX < -1)
      ) {
        supportWheel = false
        earlyReturn = true
      }

      /* Prevent overscrolling for none looping carousels */
      if (!earlyReturn && !carousel.options?.loop) {
        const scrollIndex =
          direction === 'horizontal' ? event?.deltaX : event?.deltaY
        if (
          (details.progress <= 0 && scrollIndex < 0) ||
          (details.position >= details.max && scrollIndex > 0)
        ) {
          earlyReturn = true
        }
      }

      /* Prevent default scroll functionality for carousels supporting wheel and scrolling on the correct plane */
      if (supportWheel) {
        event.preventDefault()
      }

      if (earlyReturn) {
        if (touchTimeout && wheelActive) {
          clearTimeout(touchTimeout)
          wheelActive = false
          wheelEnd(event)
        }
        return
      }

      if (!wheelActive) {
        wheelStart(event)
        wheelActive = true
      }
      wheel(event)
      clearTimeout(touchTimeout)
      touchTimeout = setTimeout(() => {
        wheelActive = false
        wheelEnd(event)
      }, 150)
    }

    carousel.on('created', () => {
      carousel.container.addEventListener('wheel', eventWheel, {
        passive: false,
      })
    })
  }
}

/** @param {HTMLElement} scrollbar */
export const useScrollbar = (scrollbar) => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    const updateScrollbar = () => {
      let numberOfSlidesShown = 0

      const numberOfSlides = carousel.track.details.slides.length
      const currentIndex = carousel.track.details.rel
      const maxIdx = carousel.track.details.maxIdx

      if (numberOfSlides <= 1) {
        scrollbar.parentElement.remove()
      }

      carousel.track.details.slides.forEach((slide) => {
        numberOfSlidesShown = numberOfSlidesShown + slide.portion
      })

      const scrollbarWidthPercentage =
        (numberOfSlidesShown / numberOfSlides) * 100

      const spaceToMove = 100 - scrollbarWidthPercentage

      const currentScrollPercentage =
        maxIdx > 0 ? (currentIndex / maxIdx) * spaceToMove : 0

      if (scrollbar && scrollbar.style) {
        scrollbar.style.inlineSize = `${scrollbarWidthPercentage}%`
        scrollbar.style.insetInlineStart = `${currentScrollPercentage}%`
      }
    }

    carousel.on('created', updateScrollbar)
    carousel.on('slideChanged', updateScrollbar)
  }
}

export const useFocusIntoView = () => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    let clipEl = null

    const getClipEl = () => {
      if (clipEl) return clipEl

      let el = carousel.container

      while (el && el !== document.body) {
        if (window.getComputedStyle(el).overflowX !== 'visible') {
          clipEl = el

          return clipEl
        }

        el = el.parentElement
      }

      clipEl = document.documentElement

      return clipEl
    }

    const isFullyVisible = (slideEl) => {
      const clipRect = getClipEl().getBoundingClientRect()
      const slideRect = slideEl.getBoundingClientRect()

      return (
        slideRect.left >= clipRect.left - 2 &&
        slideRect.right <= clipRect.right + 2
      )
    }

    const resetFocusScroll = () => {
      let el = carousel.container

      while (el && el !== document.body) {
        if (
          el.scrollLeft !== 0 &&
          window.getComputedStyle(el).overflowX === 'hidden'
        ) {
          el.scrollLeft = 0
        }

        el = el.parentElement
      }
    }

    const handleFocusIn = (event) => {
      if (carousel.options.disabled) return

      resetFocusScroll()

      const slideIndex = carousel.slides.findIndex((slideEl) =>
        slideEl.contains(event.target),
      )
      const details = carousel.track.details

      if (slideIndex === -1 || !details) return
      if (isFullyVisible(carousel.slides[slideIndex])) return

      const targetIdx = details.abs + slideIndex - details.rel

      carousel.moveToIdx(
        carousel.options.loop ? targetIdx : Math.min(targetIdx, details.maxIdx),
      )
    }

    carousel.on('created', () => {
      carousel.container.addEventListener('focusin', handleFocusIn)
    })
    carousel.on('destroyed', () => {
      carousel.container.removeEventListener('focusin', handleFocusIn)
    })
  }
}

export const useInertHiddenSlides = () => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    const updateInertSlides = () => {
      if (carousel.options.disabled) {
        carousel.slides.forEach((slideEl) => {
          slideEl.inert = false
        })

        return
      }

      carousel.track.details?.slides.forEach((slide, index) => {
        const isHidden = slide.portion < 0.5

        if (carousel.slides[index].inert !== isHidden) {
          carousel.slides[index].inert = isHidden
        }
      })
    }

    carousel.on('created', updateInertSlides)
    carousel.on('detailsChanged', updateInertSlides)
    carousel.on('optionsChanged', updateInertSlides)
    carousel.on('destroyed', () => {
      carousel.slides.forEach((slideEl) => {
        slideEl.inert = false
      })
    })
  }
}
